import { z } from 'zod';

import { DayJsSchema } from './commonSchema.ts';
import {
  AssetLinkDaysOfTheWeekArraySchema,
  AssetLinkDaysOfTheWeekSchema,
} from './resourceManagementSchema.ts';

const TimeWindowSchema = z
  .object({
    startTime: z.string(),
    endTime: z.string(),
  })
  .refine((data) => data.endTime > data.startTime, {
    error: 'End time cannot be before start time',
  });

const alphanumericRegex = /^[a-zA-Z0-9_]*$/;

export const RouteBasicDetailsSchema = z.object({
  name: z
    .string()
    .min(1, { error: 'Name cannot be empty' })
    .max(25, { error: 'Name cannot be longer than 25 characters' })
    .regex(alphanumericRegex, {
      error: "The name can't contain special characters",
    }),
  description: z.string().min(1, { error: 'Description cannot be empty' }),
  sameDay: z.boolean().optional(),
});

const RouteAvailabilityTimeWindowSchema = z.object({
  startTime: DayJsSchema,
  endTime: DayJsSchema,
});

export const RouteAvailabilityFormSchema = z.object({
  startTimeWindow: RouteAvailabilityTimeWindowSchema,
  finishTimeWindow: RouteAvailabilityTimeWindowSchema,
  serviceTimeWindow: RouteAvailabilityTimeWindowSchema,
  daysOfTheWeek: z.array(AssetLinkDaysOfTheWeekArraySchema),
});

export const RouteAvailabilitySchema = z.object({
  startTimeWindow: TimeWindowSchema,
  finishTimeWindow: TimeWindowSchema,
  serviceTimeWindow: TimeWindowSchema,
  daysOfTheWeek: AssetLinkDaysOfTheWeekSchema,
});

export const RouteRechargeSettingsSchema = z
  .object({
    rechargeSettings: z.object({
      allowRecharges: z.boolean().optional(),
      rechargePolicy: z.string().optional(),
      rechargeToCapacity: z.boolean().optional(),
    }),
    settings: z.object({
      limits: z.object({
        maxDistance: z.number().optional(),
      }),
    }),
  })
  .refine(
    (data) =>
      !data.rechargeSettings.allowRecharges ||
      (data.settings.limits.maxDistance !== undefined &&
        data.settings.limits.maxDistance >= 100000),
    {
      error: 'Max distance must be greater or equal to 100000',
      path: ['settings', 'limits', 'maxDistance'],
    }
  );

export const RouteCapacitySchema = z.object({
  capabilities: z
    .array(
      z.string('Capabilities must contain at least 1 element'),
      'Capabilities must contain at least 1 element'
    )
    .min(1, { error: 'Capabilities must contain at least 1 element' })
    .refine(
      (data) => data?.join().length < 255,
      'Combined capabilities characters length must be less than 255 characters'
    ),
  capacitySettings: z.object({
    measure1: z.number().nonnegative(),
    measure2: z.number().nonnegative(),
    measure3: z.number().nonnegative(),
    measure4: z.number().nonnegative(),
  }),
});

export type RouteBasicDetailsModel = z.infer<typeof RouteBasicDetailsSchema>;
export type RouteAvailabilityModel = z.infer<typeof RouteAvailabilitySchema>;
export type RouteRechargeSettingsModel = z.infer<
  typeof RouteRechargeSettingsSchema
>;
export type RouteCapacityModel = z.infer<typeof RouteCapacitySchema>;
export type RouteAvailabilityFormModel = z.infer<
  typeof RouteAvailabilityFormSchema
>;
export type AssetLinkDaysOfTheWeekModel = z.infer<
  typeof AssetLinkDaysOfTheWeekSchema
>;
export type AssetLinkDaysOfTheWeekArrayModel = z.infer<
  typeof AssetLinkDaysOfTheWeekArraySchema
>;
